import moment from "moment";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdMoreVert } from "react-icons/md";
import { Divider, IconButton, Menu, MenuItem, Box, Typography } from "@mui/material";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { deleteAppointment, deleteBusinessAppointment, getBusinessDetails } from "../logic/business/action";
import { errorMessage, successMessage } from "../logic/error/action";

const AppointmentTable = ({ setEditMode ,onDeleteAppointment,onCompleteAppointment,onCancelAppointment,setEditAppointment,onPatientDetails,onCustomerDetails,onSendNotification}) => {
const selector = useSelector((state) => state.business);
const selectorAuth = useSelector((state) => state.auth);
const [selectedRow, setSelectedRow] = useState(null);
const [anchorEl, setAnchorEl] = useState(null);
const openMenu = Boolean(anchorEl);
const dispatch = useDispatch();
const nav = useNavigate();

const handleClick = (event, rowData) => {
  setAnchorEl(event.currentTarget);
  setSelectedRow(rowData);
};

const handleClose = () => {
  setAnchorEl(null);
  setSelectedRow(null);
};

const handleDeleteAppointment = () => {
  if (selectedRow) {
    onDeleteAppointment(selectedRow.appointment_id);
    handleClose();
  }
};

const handleCompleteAppointment = () => {
  if (selectedRow) {
    onCompleteAppointment(selectedRow.appointment_id);
    handleClose();
  }
};

const handleCancelAppointment = () => {
  if (selectedRow) {
    onCancelAppointment(
      selectedRow.appointment_id,
      selectedRow.patient_name,
      selectedRow.contact,
      selectedRow.start_date,
      selectedRow.start_time
    );
    handleClose();
  }
};

const handleRescheduleAppointment = () => {
  if (selectedRow) {
    setEditAppointment(
      selectedRow.appointment_id,
      selectedRow.patient_name,
      selectedRow.contact
    );
    handleClose();
  }
};

const handlePatientDetails = () => {
  if (selectedRow) {
    onPatientDetails(
      selectedRow.appointment_id,
      selectedRow.patient_name,
      selectedRow.contact,
      selectedRow.patient_id
    );
    handleClose();
  }
};

const handleResendNotification = () => {
  if (selectedRow) {
    onSendNotification(
      selectedRow.appointment_id,
      selectedRow.patient_name,
      selectedRow.contact,
      selectedRow.start_date,
      selectedRow.start_time,
      selectedRow.status
    );
    handleClose();
  }
};

const columnDefs = useMemo(() => [
  {
    field: 'patient_name',
    headerName: 'Patient',
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => {
      return params.data?.patient_name || params.data?.full_name || '-';
    }
  },
  {
    field: 'staff_name',
    headerName: 'Staff',
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => {
      return params.data?.staff_name || params.data?.name || '-';
    }
  },
  {
    field: 'start_date',
    headerName: 'Date',
    flex: 1,
    minWidth: 120,
    valueFormatter: (params) => {
      if (!params.value) return '-';
      try {
        return moment(params.value).format("DD-MM-YYYY");
      } catch (e) {
        return params.value;
      }
    }
  },
  {
    field: 'timings',
    headerName: 'Timings',
    flex: 1,
    minWidth: 180,
    valueGetter: (params) => {
      if (!params.data || !params.data.start_time || !params.data.end_time) {
        return '-';
      }
      try {
        const startTime = moment(params.data.start_time, ['HH:mm:ss', 'HH:mm', 'hh:mm A']).format('hh:mm A');
        const endTime = moment(params.data.end_time, ['HH:mm:ss', 'HH:mm', 'hh:mm A']).format('hh:mm A');
        return `${startTime} - ${endTime}`;
      } catch (e) {
        return `${params.data.start_time} - ${params.data.end_time}`;
      }
    }
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    minWidth: 120,
    cellRenderer: (params) => {
      const status = params.value || '-';
      const statusColors = {
        'pending': '#f59e0b',
        'confirmed': '#10b981',
        'completed': '#3b82f6',
        'cancelled': '#ef4444',
        'rescheduled': '#8b5cf6'
      };
      const color = statusColors[status.toLowerCase()] || '#6b7280';
      return (
        <Box
          component="span"
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: `${color}20`,
            color: color,
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'capitalize',
            display: 'inline-block'
          }}
        >
          {status}
        </Box>
      );
    }
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    cellRenderer: (params) => {
      return (
        <IconButton 
          size="small" 
          onClick={(e) => handleClick(e, params.data)}
        >
          <MdMoreVert />
        </IconButton>
      );
    },
    sortable: false,
    filter: false
  }
], []);

const defaultColDef = useMemo(() => ({
  sortable: true,
  filter: true,
  resizable: true
}), []);

return (
  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <Box 
      className="ag-theme-alpine" 
      sx={{ 
        width: '100%',
        height: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& .ag-root-wrapper': {
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        },
        '& .ag-root': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        },
        '& .ag-body-viewport': {
          flex: 1,
          overflowY: 'auto !important',
        },
        '& .ag-body': {
          flex: 1,
          minHeight: 0,
        },
        '& .ag-header': {
          backgroundColor: '#f9fafb',
          borderBottom: '2px solid #e5e7eb',
          minHeight: '48px !important',
        },
        '& .ag-header-cell': {
          fontWeight: 600,
          color: '#1f2937',
          fontSize: '0.875rem',
          padding: '12px 16px',
          borderRight: '1px solid #e5e7eb',
          '&:last-child': {
            borderRight: 'none',
          },
        },
        '& .ag-header-cell-text': {
          fontWeight: 600,
          color: '#1f2937',
        },
        '& .ag-row': {
          borderBottom: '1px solid #f3f4f6',
          minHeight: '48px !important',
          '&:hover': {
            backgroundColor: '#f9fafb',
          },
        },
        '& .ag-row-even': {
          backgroundColor: '#ffffff',
        },
        '& .ag-row-odd': {
          backgroundColor: '#fafafa',
        },
        '& .ag-cell': {
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.875rem',
          color: '#374151',
          padding: '12px 16px',
          borderRight: '1px solid #f3f4f6',
          '&:last-child': {
            borderRight: 'none',
          },
        },
        '& .ag-row-selected': {
          backgroundColor: 'rgba(102, 126, 234, 0.1) !important',
        },
        '& .ag-paging-panel': {
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          padding: '12px 16px',
          minHeight: '48px',
        },
        '& .ag-paging-button': {
          color: '#667eea',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
          },
        },
        '& .ag-paging-button.ag-disabled': {
          color: '#9ca3af',
        },
      }}
    >
      <AgGridReact
        rowData={selector?.appointments || []}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        pagination={true}
        paginationPageSize={10}
        animateRows={true}
        theme={"legacy"}
        rowSelection="single"
        suppressRowClickSelection={true}
        domLayout="normal"
        noRowsOverlayComponent={() => (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
              No appointments found
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
              Try selecting a different date or create a new appointment
            </Typography>
          </Box>
        )}
      />
    </Box>

    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={openMenu}
      onClose={handleClose}
      onClick={handleClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          mt: 1.5,
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <MenuItem onClick={handlePatientDetails}>
        Patient Details
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleRescheduleAppointment}>
        Re-Schedule
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleCancelAppointment}>
        Cancel Appointment
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleCompleteAppointment}>
        Complete Appointment
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleDeleteAppointment}>
        Delete Appointment
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleResendNotification}>
        Send Notification
      </MenuItem>
    </Menu>
  </Box>
  
//     <div className="table-wrapper">
//       <div className="table-container">
//         <div className="td-head lg:w-full justify-between">
//           {
// selectorAuth?.user?.role==='supper_admin'??
//             <p className="td-element">Id</p>
//           }
//           <p className="td-element">Customer Name</p>
//           <p className="td-element">Doctor Name</p>
//           <p className="td-element">Date</p>
//           <p className="td-element">Start Time</p>
//           <p className="td-element">End Time</p>
//           <p className="td-element">Action</p>
//         </div>
//         <div className="flex-1">
//           {
//             // JSON.stringify(selector?.data)
//             // selector?.data?.map((element,i)=><p key={i}>hi</p>)
//             selector?.appointments?.map((e, i) => (
//               <div
//                 className="td-row lg:w-full justify-between"
//                 key={i}
//               >
//                 {
//                   selectorAuth?.user?.role==='supper_admin'??
//                   <p className="td-element highlited">
//                   <Link to={"/business/" + e?.appointment_id}>
//                     {e?.appointment_id}
//                   </Link>
//                 </p>
//                 }
//                 <p className="td-element">{e?.patient_name}</p>
//                 <p className="td-element">{e?.staff_name}</p>
//                 <p className="td-element">
//                   {moment(e?.start_date).format("DD-MM-YYYY")}
//                 </p>
//                 <p className="td-element">{e?.start_time}</p>
//                 <p className="td-element">{e?.end_time}</p>
//                 {/* <p className="td-element">{e?.email === ''?'-':e?.email}</p> */}
//                 <p className="td-element">
//                   <span className="flex gap-4 justify-center items-center">
//                     <p
//                       className="cursor-pointer highlited"
//                       onClick={()=>setEditMode(e)}
//                     >Reschedule</p>

// <MdDelete color='red'/>
//                   </span>
//                 </p>
//               </div>
//             ))
//           }
//         </div>
//       </div>
//     </div>
);
};

export default AppointmentTable;
